/**
 * Admin Amazon Feed detail page.
 *
 * Layout (per admin spec 2026-06-09):
 *   1. Overall totals strip at top — clicks/transactions/commission
 *      across ALL Amazon brands in this feed (all countries combined).
 *   2. "Per-country" cards — Amazon US / Amazon UK side-by-side, so
 *      admin can differentiate within the same feed.
 *   3. Publishers running this feed — list with each pub's stats.
 *      Click a row to drill into per-pub by-day numbers.
 *
 * SALE AMOUNT is intentionally hidden (PubAdHub feed doesn't expose
 * order value, only commission — admin chose to omit).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const RANGES = [
  { k: 7, label: "Last 7 days" },
  { k: 14, label: "Last 14 days" },
  { k: 30, label: "Last 30 days" },
  { k: 90, label: "Last 90 days" },
];

const fmtMoney = (n) => { const v = Number(n || 0); return v ? `$${v.toFixed(2)}` : "$0"; };
const fmtPct = (n) => { const v = Number(n || 0); return v ? `${v.toFixed(2)}%` : "0%"; };
const fmtNum = (n) => (n || 0).toLocaleString();

function StatCard({ label, value, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="text-[11px] tracking-[0.15em] uppercase font-bold text-slate-500">{label}</div>
        <div className="text-2xl font-bold text-slate-900 mt-1.5 tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

function StatsStrip({ totals, prefix }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <StatCard label="Clicks" value={fmtNum(totals?.clicks)} testid={`${prefix}-clicks`} />
      <StatCard label="Transactions" value={fmtNum(totals?.transactions)} testid={`${prefix}-txns`} />
      <StatCard label="Commission" value={fmtMoney(totals?.commission)} testid={`${prefix}-comm`} />
      <StatCard label="EPC" value={Number(totals?.epc || 0).toFixed(2)} testid={`${prefix}-epc`} />
      <StatCard label="Conv. Rate" value={fmtPct(totals?.conversion_rate)} testid={`${prefix}-cr`} />
    </div>
  );
}

export default function AdminAmazonFeed() {
  const { slug } = useParams();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPub, setExpandedPub] = useState(null);
  const [pubData, setPubData] = useState({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setExpandedPub(null);
    setPubData({});
    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - (days - 1));
    const fmt = (d) => d.toISOString().slice(0, 10);
    API.get(`/admin/amazon-feeds/${slug}?start_date=${fmt(start)}&end_date=${fmt(end)}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e?.message || "Failed to load"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, days]);

  const togglePub = async (pubId) => {
    if (expandedPub === pubId) {
      setExpandedPub(null);
      return;
    }
    setExpandedPub(pubId);
    if (pubData[pubId]) return;
    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - (days - 1));
    const fmt = (d) => d.toISOString().slice(0, 10);
    try {
      const r = await API.get(
        `/admin/amazon-feeds/${slug}/publishers/${pubId}?start_date=${fmt(start)}&end_date=${fmt(end)}`,
      );
      setPubData((p) => ({ ...p, [pubId]: r.data }));
    } catch (e) { /* leave row collapsed if it fails */ }
  };

  const label = data?.label || "Amazon Feed";
  const overall = data?.overall?.totals || {};
  const byBrand = data?.by_brand || [];
  const publishers = data?.publishers || [];

  return (
    <div className="p-6 space-y-6" data-testid="amazon-feed-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">{label}</h1>
          <p className="text-sm text-slate-500 mt-1">
            Overall performance and per-publisher breakdown for this Amazon feed source.
          </p>
        </div>
        <div className="flex gap-2">
          {RANGES.map((r) => (
            <Button
              key={r.k} variant={days === r.k ? "default" : "outline"} size="sm"
              onClick={() => setDays(r.k)} data-testid={`amazon-feed-range-${r.k}`}
            >
              {r.label}
            </Button>
          ))}
        </div>
      </div>

      {error && (
        <Card className="border-rose-200 bg-rose-50"><CardContent className="p-4 text-sm text-rose-700">{error}</CardContent></Card>
      )}

      {/* 1. Overall totals */}
      <section>
        <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">Overall — all countries</h2>
        {loading ? <Skeleton className="h-24 w-full" /> : <StatsStrip totals={overall} prefix="overall" />}
      </section>

      {/* 2. Per-brand / per-country breakdown */}
      {byBrand.length > 1 && (
        <section>
          <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">Per Country</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {byBrand.map((b) => (
              <Card key={b.brand_id} data-testid={`amazon-feed-brand-${b.country || b.brand_id}`}>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <span>{b.country ? `Amazon ${b.country}` : b.brand_name}</span>
                    {b.country && <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{b.country}</span>}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div><div className="text-slate-500 text-xs">Clicks</div><div className="font-bold tabular-nums">{fmtNum(b.totals?.clicks)}</div></div>
                    <div><div className="text-slate-500 text-xs">Txns</div><div className="font-bold tabular-nums">{fmtNum(b.totals?.transactions)}</div></div>
                    <div><div className="text-slate-500 text-xs">Commission</div><div className="font-bold tabular-nums">{fmtMoney(b.totals?.commission)}</div></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* 3. Publishers running this feed */}
      <section>
        <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">Publishers Running ({publishers.length})</h2>
        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : publishers.length === 0 ? (
          <Card><CardContent className="p-6 text-sm text-slate-400 text-center">No publishers assigned to this feed yet.</CardContent></Card>
        ) : (
          <div className="space-y-2">
            {publishers.map((p) => {
              const isOpen = expandedPub === p.publisher_id;
              const pd = pubData[p.publisher_id];
              return (
                <Card key={p.publisher_id} data-testid={`amazon-feed-pub-${p.sub_id}`}>
                  <button
                    type="button"
                    onClick={() => togglePub(p.publisher_id)}
                    className="w-full p-4 flex items-center justify-between hover:bg-slate-50 text-left transition"
                    data-testid={`amazon-feed-pub-row-${p.sub_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-700 text-xs font-bold flex items-center justify-center font-mono">
                        {p.sub_id}
                      </div>
                      <div>
                        <div className="font-bold text-slate-900">{p.name}</div>
                        <div className="text-xs text-slate-500 font-mono">{p.sub_id}</div>
                      </div>
                    </div>
                    <div className="flex gap-6 items-center text-right text-sm tabular-nums">
                      <div><div className="text-[10px] uppercase font-bold text-slate-500">Clicks</div><div className="font-bold">{fmtNum(p.totals?.clicks)}</div></div>
                      <div><div className="text-[10px] uppercase font-bold text-slate-500">Txns</div><div className="font-bold">{fmtNum(p.totals?.transactions)}</div></div>
                      <div><div className="text-[10px] uppercase font-bold text-slate-500">Commission</div><div className="font-bold">{fmtMoney(p.totals?.commission)}</div></div>
                      <div><div className="text-[10px] uppercase font-bold text-slate-500">EPC</div><div className="font-bold">{Number(p.totals?.epc || 0).toFixed(2)}</div></div>
                      <div className="text-slate-400 text-xs">{isOpen ? "▼" : "▶"}</div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-slate-100 p-4 bg-slate-50/50">
                      {!pd ? <Skeleton className="h-16 w-full" /> : (
                        <>
                          {/* per-country split for this pub */}
                          {pd.by_brand?.length > 1 && (
                            <div className="grid grid-cols-2 gap-3 mb-4">
                              {pd.by_brand.map((b) => (
                                <div key={b.brand_id} className="bg-white rounded p-3 border border-slate-200">
                                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500">
                                    {b.country ? `Amazon ${b.country}` : b.brand_name}
                                  </div>
                                  <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                                    <div>Clicks: <span className="font-bold">{fmtNum(b.totals?.clicks)}</span></div>
                                    <div>Txns: <span className="font-bold">{fmtNum(b.totals?.transactions)}</span></div>
                                    <div>Comm: <span className="font-bold">{fmtMoney(b.totals?.commission)}</span></div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* by-day table */}
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500 border-b border-slate-200">
                                <th className="text-left py-2">Date</th>
                                <th className="text-right py-2">Clicks</th>
                                <th className="text-right py-2">Txns</th>
                                <th className="text-right py-2">Commission</th>
                                <th className="text-right py-2">EPC</th>
                                <th className="text-right py-2">Conv. Rate</th>
                              </tr>
                            </thead>
                            <tbody className="font-mono tabular-nums">
                              {(pd.rows || []).map((r) => (
                                <tr key={r.date} className="border-b border-slate-100">
                                  <td className="py-1.5 text-slate-700">{r.date}</td>
                                  <td className="text-right py-1.5">{fmtNum(r.clicks)}</td>
                                  <td className="text-right py-1.5">{fmtNum(r.transactions)}</td>
                                  <td className="text-right py-1.5">{fmtMoney(r.commission)}</td>
                                  <td className="text-right py-1.5">{Number(r.epc || 0).toFixed(2)}</td>
                                  <td className="text-right py-1.5">{fmtPct(r.conversion_rate)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
